import { EnrollmentStatus, OnboardingPeriodDto } from '@zorro/clients';
import {
  formatDateEnLocale,
  getDaysLeft,
  isDateOnOrBeforeToday,
} from '@zorro/shared/formatters';
import { $enum } from 'ts-enum-util';

const formatDays = (count: number): string =>
  `${count} ${count === 1 ? 'day' : 'days'}`;

const getWindowClosesMessage = (date: string, prefix: string): string => {
  const daysUntil = getDaysLeft(date);
  return `${prefix}. Window closes in ${formatDays(daysUntil)}`;
};

export const getEnrollmentSubtitle = (
  enrollmentStatus: EnrollmentStatus,
  onboardingPeriod: OnboardingPeriodDto,
  employeeName: string,
  isDon: boolean,
  majorMedicalSubmittedAt?: string | null
): string => {
  const daysUntilClose = getDaysLeft(onboardingPeriod.onboardingUntil);
  const daysUntilOpen = getDaysLeft(onboardingPeriod.onboardingFrom);

  const isElectionWindowOpen = isDateOnOrBeforeToday(
    onboardingPeriod.onboardingFrom
  );
  const pendingElectionWindowMessage = isElectionWindowOpen
    ? `Window open but on hold behind another election. Closes in ${formatDays(daysUntilClose)}.`
    : `Election window opens in ${formatDays(daysUntilOpen)}`;

  const hasNotStartedMessage = getWindowClosesMessage(
    onboardingPeriod.onboardingUntil,
    `${employeeName} hasn't started their election`
  );

  const submittedDate = majorMedicalSubmittedAt
    ? formatDateEnLocale(majorMedicalSubmittedAt)
    : 'N/A';

  return $enum.mapValue(enrollmentStatus).with<string>({
    [EnrollmentStatus.PENDING_ELECTION_WINDOW]: pendingElectionWindowMessage,
    [EnrollmentStatus.ELECTION_ACTIVE_HAS_NOT_STARTED]: hasNotStartedMessage,
    [EnrollmentStatus.ELECTION_ACTIVE]: hasNotStartedMessage,
    [EnrollmentStatus.ELECTION_ACTIVE_STARTED]: `Plan selection started but not submitted. Closes in ${formatDays(daysUntilClose)}`,
    [EnrollmentStatus.ELECTION_SUBMITTED]: isDon
      ? `Election submitted on ${submittedDate}. Awaiting carrier enrollment`
      : `Election submitted on ${submittedDate}. Ready to send to carrier`,
    [EnrollmentStatus.ENROLLMENT_IN_PROCESS]: 'Carrier enrollment in progress',
    [EnrollmentStatus.ENROLLMENT_CONFIRMED]: `Plan enrolled. Coverage starts on ${formatDateEnLocale(onboardingPeriod.effectiveFrom)}`,
    [EnrollmentStatus.ACTIVE_COVERAGE]: `Coverage active since ${formatDateEnLocale(onboardingPeriod.effectiveFrom)}`,
    [EnrollmentStatus.COVERAGE_ENDED]: `Coverage ended on ${formatDateEnLocale(onboardingPeriod.effectiveUntil)}`,
    [EnrollmentStatus.DEADLINE_PASSED]: isDon
      ? 'Election window passed without a submission'
      : 'Election window passed without a submission. Change election window if needed',
    [EnrollmentStatus.WAIVED_ELECTION]: onboardingPeriod.waivedAt
      ? `Employee waived their election on ${formatDateEnLocale(onboardingPeriod.waivedAt)}.`
      : 'Employee waived their election.',
    [EnrollmentStatus.WAIVED_COVERAGE]:
      'Employee waived coverage for this plan period',
    [EnrollmentStatus.NO_ENROLLMENTS]: '',
  });
};
